/** @type {2} */
export const RCA_MEASUREMENTS_CONFIG_VERSION = 2;

export const RCA_MEASUREMENTS_CONFIG_STORAGE_KEY =
  "oneview.rca.measurements.config";

/** @typedef {'drift'|'keySensors'|'remaining'} RcaMeasurementSectionId */

/** @type {readonly RcaMeasurementSectionId[]} */
const RCA_MEASUREMENT_SECTION_IDS = ["drift", "keySensors", "remaining"];

/** @type {Record<RcaMeasurementSectionId, string>} */
export const RCA_MEASUREMENT_SECTION_LABELS = {
  drift: "Pre-Shutdown Drift Summary",
  keySensors: "Key Sensor Signals",
  remaining: "Show Remaining Sensors",
};

/** @type {readonly RcaMeasurementSectionId[]} */
const RCA_MEASUREMENTS_DEFAULT_SECTION_ORDER = [
  "drift",
  "keySensors",
  "remaining",
];

/** @type {Record<RcaMeasurementSectionId, boolean>} */
const RCA_MEASUREMENTS_DEFAULT_SECTION_EXPANDED = {
  drift: true,
  keySensors: false,
  remaining: false,
};

/** @typedef {Object} RcaMeasurementsConfig
 * @property {number} version Schema version for migrations.
 * @property {boolean} useAlarmLimits Key-trend scoring and chart alarm bands.
 * @property {number} keyTrendScoreThreshold Key trends: total &gt;= threshold; remaining: total &lt; threshold.
 * @property {number} zScoreThreshold Z-score scale for no-alarm key-trend z-score behavior.
 * @property {RcaMeasurementSectionId[]} sectionOrder Display order of measurement sections.
 * @property {Record<RcaMeasurementSectionId, boolean>} sectionExpanded Collapsed state per section.
 */

/** @type {RcaMeasurementsConfig} */
export const RCA_MEASUREMENTS_CONFIG_DEFAULTS = {
  version: RCA_MEASUREMENTS_CONFIG_VERSION,
  useAlarmLimits: true,
  keyTrendScoreThreshold: 20,
  zScoreThreshold: 2,
  sectionOrder: [...RCA_MEASUREMENTS_DEFAULT_SECTION_ORDER],
  sectionExpanded: { ...RCA_MEASUREMENTS_DEFAULT_SECTION_EXPANDED },
};

/**
 * @param {unknown} id
 * @returns {id is RcaMeasurementSectionId}
 */
function isRcaMeasurementSectionId(id) {
  return id === "drift" || id === "keySensors" || id === "remaining";
}

/**
 * @param {unknown} order
 * @returns {RcaMeasurementSectionId[]}
 */
function normalizeSectionOrder(order) {
  const seen = new Set();
  const normalized = [];
  if (Array.isArray(order)) {
    for (const id of order) {
      if (isRcaMeasurementSectionId(id) && !seen.has(id)) {
        seen.add(id);
        normalized.push(id);
      }
    }
  }
  for (const id of RCA_MEASUREMENT_SECTION_IDS) {
    if (!seen.has(id)) normalized.push(id);
  }
  return normalized;
}

/**
 * @param {unknown} expanded
 * @param {RcaMeasurementSectionId[]} sectionOrder
 * @returns {Record<RcaMeasurementSectionId, boolean>}
 */
function normalizeSectionExpanded(expanded, sectionOrder) {
  const src =
    expanded && typeof expanded === "object" && !Array.isArray(expanded)
      ? expanded
      : {};
  /** @type {Record<RcaMeasurementSectionId, boolean>} */
  const out = { ...RCA_MEASUREMENTS_DEFAULT_SECTION_EXPANDED };
  for (const id of sectionOrder) {
    if (typeof src[id] === "boolean") out[id] = src[id];
  }
  return out;
}

/**
 * @param {unknown} value
 * @param {number} fallback
 */
function clampNonNegative(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/**
 * @param {unknown} raw
 * @returns {RcaMeasurementsConfig}
 */
function normalizeRcaMeasurementsConfig(raw) {
  const src = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const sectionOrder = normalizeSectionOrder(src.sectionOrder);
  return {
    version: RCA_MEASUREMENTS_CONFIG_VERSION,
    useAlarmLimits: src.useAlarmLimits !== false,
    keyTrendScoreThreshold: clampNonNegative(
      src.keyTrendScoreThreshold,
      RCA_MEASUREMENTS_CONFIG_DEFAULTS.keyTrendScoreThreshold
    ),
    zScoreThreshold: clampNonNegative(
      src.zScoreThreshold,
      RCA_MEASUREMENTS_CONFIG_DEFAULTS.zScoreThreshold
    ),
    sectionOrder,
    sectionExpanded: normalizeSectionExpanded(
      src.sectionExpanded,
      sectionOrder
    ),
  };
}

/**
 * @returns {RcaMeasurementsConfig}
 */
export function loadRcaMeasurementsConfig() {
  try {
    const raw = localStorage.getItem(RCA_MEASUREMENTS_CONFIG_STORAGE_KEY);
    if (!raw) return { ...RCA_MEASUREMENTS_CONFIG_DEFAULTS };
    return normalizeRcaMeasurementsConfig(JSON.parse(raw));
  } catch {
    return { ...RCA_MEASUREMENTS_CONFIG_DEFAULTS };
  }
}

/**
 * @param {RcaMeasurementsConfig} config
 */
function saveRcaMeasurementsConfig(config) {
  try {
    localStorage.setItem(
      RCA_MEASUREMENTS_CONFIG_STORAGE_KEY,
      JSON.stringify(normalizeRcaMeasurementsConfig(config))
    );
  } catch {
    // ignore quota / private mode
  }
}

/**
 * @param {Partial<RcaMeasurementsConfig>} patch
 * @returns {RcaMeasurementsConfig}
 */
export function patchRcaMeasurementsConfig(patch) {
  const current = loadRcaMeasurementsConfig();
  const merged = { ...current, ...patch };
  if (patch.sectionOrder != null) {
    merged.sectionOrder = normalizeSectionOrder(patch.sectionOrder);
  }
  if (patch.sectionExpanded != null) {
    merged.sectionExpanded = normalizeSectionExpanded(
      { ...current.sectionExpanded, ...patch.sectionExpanded },
      merged.sectionOrder
    );
  }
  const next = normalizeRcaMeasurementsConfig(merged);
  saveRcaMeasurementsConfig(next);
  return next;
}

/**
 * @param {RcaMeasurementSectionId[]} order
 * @param {RcaMeasurementSectionId} id
 * @param {'up'|'down'} direction
 * @returns {RcaMeasurementSectionId[]}
 */
export function moveSectionInOrder(order, id, direction) {
  const idx = order.indexOf(id);
  if (idx < 0) return order;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= order.length) return order;
  const next = [...order];
  [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
  return next;
}
